(() => {
  const API = '/apps/em-reservations';

  function money(value) {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(Number(value || 0));
  }

  function selectedVariantId(container) {
    const formId = container.dataset.productFormId;
    const form = document.getElementById(formId);
    return form?.querySelector('input[name="id"]')?.value || null;
  }

  function expiryText(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-CA', {
      weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
    }).format(date);
  }

  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>'"]/g, (char) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
    })[char]);
  }

  function render(container, reservation, message) {
    const status = container.querySelector('[data-em-reservation-status]');
    if (!status || !reservation) return;
    const shipping = reservation.freeShipping || {};
    const items = reservation.items || [];
    const count = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const expires = expiryText(reservation.reserveInventoryUntil);
    const itemRows = items.map((item) => `
      <div class="em-reservation__item">
        <div class="em-reservation__item-copy">
          <span class="em-reservation__item-title">${escapeHtml(item.productTitle)}</span>
          ${item.variantTitle && item.variantTitle !== 'Default Title' ? `<span class="em-reservation__item-variant">${escapeHtml(item.variantTitle)}</span>` : ''}
        </div>
        <div class="em-reservation__item-actions">
          ${item.variantId ? `<div class="em-reservation__quantity" aria-label="Quantity for ${escapeHtml(item.productTitle)}"><button type="button" class="em-reservation__qty-button" data-em-qty-change data-variant-id="${escapeHtml(item.variantId)}" data-quantity="${Math.max(1, Number(item.quantity || 1) - 1)}" ${Number(item.quantity || 1) <= 1 ? 'disabled' : ''} aria-label="Decrease quantity">−</button><span class="em-reservation__qty-number">${Number(item.quantity || 0)}</span><button type="button" class="em-reservation__qty-button" data-em-qty-change data-variant-id="${escapeHtml(item.variantId)}" data-quantity="${Math.min(5, Number(item.quantity || 1) + 1)}" ${Number(item.quantity || 1) >= 5 ? 'disabled' : ''} aria-label="Increase quantity">+</button></div><button type="button" class="em-reservation__remove" data-em-remove data-variant-id="${escapeHtml(item.variantId)}">Remove</button>` : `<span class="em-reservation__qty">Qty ${Number(item.quantity || 0)}</span>`}
        </div>
      </div>
    `).join('');

    status.hidden = false;
    status.className = 'em-reservation__status em-reservation__status--success';
    status.innerHTML = `
      <div class="em-reservation__heading">
        <div>
          <strong>${escapeHtml(message || 'Your BrüMate reservation')}</strong>
          <span class="em-reservation__summary">${count} item${count === 1 ? '' : 's'} · ${money(reservation.subtotal)}</span>
        </div>
        ${expires ? `<span class="em-reservation__expiry">Held until ${escapeHtml(expires)}</span>` : ''}
      </div>
      <div class="em-reservation__items">${itemRows}</div>
      <div class="em-reservation__shipping">
        <div class="em-reservation__progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Number(shipping.progress || 0)}"><span style="--em-progress:${Number(shipping.progress || 0)}%"></span></div>
        <span>${escapeHtml(shipping.message || '')}</span>
      </div>
      ${reservation.checkoutUrl ? '<button type="button" class="button button--full-width button--primary em-reservation__checkout" data-em-checkout>Checkout reserved items</button>' : ''}
      <a class="em-reservation__view" href="/pages/my-reservations">View my reserved items</a>
    `;
  }

  function renderError(container, message, loginRequired) {
    const status = container.querySelector('[data-em-reservation-status]');
    if (!status) return;
    status.hidden = false;
    status.className = 'em-reservation__status em-reservation__status--error';
    status.innerHTML = loginRequired
      ? `${escapeHtml(message)} <a href="/account/login?return_url=${encodeURIComponent(location.pathname + location.search)}">Sign in</a>`
      : escapeHtml(message);
  }

  function renderNotice(container, message) {
    const status = container.querySelector('[data-em-reservation-status]');
    if (!status || !message) return;
    status.hidden = false;
    const existing = status.querySelector('[data-em-reservation-notice]');
    if (existing) existing.remove();
    status.insertAdjacentHTML('afterbegin', `<div class="em-reservation__notice" data-em-reservation-notice>${escapeHtml(message)}</div>`);
  }

  async function getStatus(container) {
    try {
      const response = await fetch(API, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      const data = await response.json();
      if (response.ok && data.active) {
        render(container, data, 'Your current reservation');
        return data;
      }
      return data;
    } catch (_) {
      return null;
    }
  }

  async function reserve(container, button) {
    const variantId = selectedVariantId(container);
    if (!variantId) return renderError(container, 'Please choose a product option first.');
    const original = button.textContent;
    button.disabled = true;
    button.textContent = 'Reserving…';
    try {
      const response = await fetch(`${API}/reserve`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ variantId, quantity: 1 })
      });
      const data = await response.json();
      if (!response.ok) throw Object.assign(new Error(data.error || 'Unable to reserve this item.'), { loginRequired: data.loginRequired, status: response.status, availableQuantity: data.availableQuantity });
      render(container, data.reservation, 'Reserved for 7 days');
      button.textContent = 'Reserved — add another';
    } catch (error) {
      if (error.status === 409) {
        await getStatus(container);
        renderNotice(container, error.message || 'No more of this item are currently available to reserve.');
        button.textContent = '✓ Reserved — maximum available';
        button.disabled = true;
        return;
      }
      renderError(container, error.message || 'Unable to reserve this item.', error.loginRequired);
      button.textContent = original;
    } finally {
      if (button.textContent !== '✓ Reserved — maximum available') button.disabled = false;
    }
  }

  async function removeItem(container, button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;
    button.disabled = true;
    button.textContent = 'Removing…';
    try {
      const response = await fetch(`${API}/remove`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ variantId })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to remove this item.');
      render(container, data.reservation || data, 'Reservation updated');
    } catch (error) {
      renderError(container, error.message || 'Unable to update your reservation.');
    }
  }

  async function updateQuantity(container, button) {
    const variantId = button.dataset.variantId;
    const quantity = Number(button.dataset.quantity);
    if (!variantId || !Number.isFinite(quantity)) return;
    button.disabled = true;
    try {
      const response = await fetch(`${API}/update`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ variantId, quantity })
      });
      const data = await response.json();
      if (!response.ok) throw Object.assign(new Error(data.error || 'Unable to update quantity.'), { status: response.status, availableQuantity: data.availableQuantity });
      render(container, data.reservation || data, 'Reservation updated');
    } catch (error) {
      if (error.status === 409) {
        await getStatus(container);
        renderNotice(container, error.message || 'No more of this item are currently available to reserve.');
        return;
      }
      renderError(container, error.message || 'Unable to update your reservation.');
    }
  }

  async function checkout(container, button) {
    const original = button?.textContent;
    if (button) { button.disabled = true; button.textContent = 'Opening checkout…'; }
    try {
      const response = await fetch(`${API}/checkout`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      const data = await response.json();
      if (!response.ok || !data.checkoutUrl) throw new Error(data.error || 'Checkout is not available yet.');
      window.location.assign(data.checkoutUrl);
    } catch (error) {
      if (button) { button.disabled = false; button.textContent = original || 'Checkout reserved items'; }
      renderError(container, error.message || 'Unable to open checkout.');
    }
  }


  function renderPage(page, reservation) {
    const target = page.querySelector('[data-em-reservations-page-status]');
    if (!target) return;
    if (!reservation?.active) {
      target.innerHTML = `
        <div class="em-reservations-page__empty">
          <h2>No reserved items yet</h2>
          <p>When you reserve an in-stock BrüMate, it will appear here and be held for 7 days.</p>
          <a class="button button--primary" href="/collections/brumate">Shop BrüMate</a>
        </div>`;
      return;
    }
    const shipping = reservation.freeShipping || {};
    const expires = expiryText(reservation.reserveInventoryUntil);
    const rows = (reservation.items || []).map(item => `
      <div class="em-reservations-page__item">
        <div class="em-reservations-page__item-visual">
          ${item.imageUrl ? `<img class="em-reservations-page__item-image" src="${escapeHtml(item.imageUrl)}" alt="" loading="lazy" width="72" height="72">` : '<div class="em-reservations-page__item-image em-reservations-page__item-image--placeholder"></div>'}
          <div class="em-reservations-page__item-main">
            ${item.productUrl ? `<a class="em-reservations-page__item-link" href="${escapeHtml(item.productUrl)}"><strong>${escapeHtml(item.productTitle)}</strong></a>` : `<strong>${escapeHtml(item.productTitle)}</strong>`}
            ${item.variantTitle && item.variantTitle !== 'Default Title' ? `<span>${escapeHtml(item.variantTitle)}</span>` : ''}
          </div>
        </div>
        <div class="em-reservations-page__item-meta">
          ${item.variantId ? `<div class="em-reservation__quantity" aria-label="Quantity for ${escapeHtml(item.productTitle)}"><button type="button" class="em-reservation__qty-button" data-em-page-qty data-variant-id="${escapeHtml(item.variantId)}" data-quantity="${Math.max(1, Number(item.quantity || 1) - 1)}" ${Number(item.quantity || 1) <= 1 ? 'disabled' : ''} aria-label="Decrease quantity">−</button><span class="em-reservation__qty-number">${Number(item.quantity || 0)}</span><button type="button" class="em-reservation__qty-button" data-em-page-qty data-variant-id="${escapeHtml(item.variantId)}" data-quantity="${Math.min(5, Number(item.quantity || 1) + 1)}" ${Number(item.quantity || 1) >= 5 ? 'disabled' : ''} aria-label="Increase quantity">+</button></div>` : `<span>Qty ${Number(item.quantity || 0)}</span>`}
          ${item.price != null ? `<strong>${money(Number(item.price) * Number(item.quantity || 0))}</strong>` : ''}
          ${item.variantId ? `<button type="button" class="em-reservation__remove" data-em-page-remove data-variant-id="${escapeHtml(item.variantId)}">Remove</button>` : ''}
        </div>
      </div>`).join('');
    target.innerHTML = `
      <div class="em-reservations-page__summary-row">
        <div><span class="em-reservations-page__label">Reservation subtotal</span><strong>${money(reservation.subtotal)}</strong></div>
        ${expires ? `<div class="em-reservations-page__hold"><span class="em-reservations-page__label">Held until</span><strong>${escapeHtml(expires)}</strong></div>` : ''}
      </div>
      <div class="em-reservations-page__items">${rows}</div>
      <div class="em-reservations-page__shipping">
        <div class="em-reservation__progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Number(shipping.progress || 0)}"><span style="--em-progress:${Number(shipping.progress || 0)}%"></span></div>
        <strong>${escapeHtml(shipping.message || '')}</strong>
        <span>Free Canada-wide shipping on orders $100+.</span>
      </div>
      <div class="em-reservations-page__actions">
        <a class="button button--secondary" href="/collections/brumate">Continue shopping</a>
        ${reservation.checkoutUrl ? '<button type="button" class="button button--primary" data-em-page-checkout>Checkout reserved items</button>' : ''}
      </div>`;
  }

  async function enrichReservationItems(reservation) {
    if (!reservation?.items?.length) return reservation;
    await Promise.all(reservation.items.map(async (item) => {
      const numericId = String(item.variantId || '').split('/').pop();
      if (!numericId || !/^\d+$/.test(numericId)) return;
      try {
        const response = await fetch(`/variants/${numericId}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (response.ok) {
          const variant = await response.json();
          item.imageUrl = variant.featured_image?.src || variant.featured_image || item.imageUrl || '';
          if (item.price == null) item.price = Number(variant.price || 0) / 100;
        }
      } catch (_) {}
      if (item.productHandle) {
        item.productUrl = `/products/${encodeURIComponent(item.productHandle)}`;
        if (!item.imageUrl) {
          try {
            const response = await fetch(`/products/${encodeURIComponent(item.productHandle)}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
            if (response.ok) {
              const product = await response.json();
              item.imageUrl = product.featured_image || product.images?.[0] || '';
            }
          } catch (_) {}
        }
      }
    }));
    return reservation;
  }

  async function loadPage(page) {
    const target = page.querySelector('[data-em-reservations-page-status]');
    try {
      const response = await fetch(API, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      const data = await response.json();
      if (response.status === 401 || data.loginRequired) {
        target.innerHTML = `<div class="em-reservations-page__empty"><h2>Sign in to view your reservations</h2><p>Your BrüMate reservation is connected to your customer account.</p><a class="button button--primary" href="/account/login?return_url=${encodeURIComponent('/pages/my-reservations')}">Sign in</a></div>`;
        return;
      }
      if (!response.ok) throw new Error(data.error || 'Unable to load your reservation.');
      await enrichReservationItems(data);
      renderPage(page, data);
    } catch (error) {
      target.innerHTML = `<div class="em-reservations-page__empty"><h2>We couldn't load your reservation</h2><p>${escapeHtml(error.message || 'Please try again.')}</p></div>`;
    }
  }

  function initPage(page) {
    if (page.dataset.emReady === 'true') return;
    page.dataset.emReady = 'true';
    page.addEventListener('click', async (event) => {
      const checkoutButton = event.target.closest('[data-em-page-checkout]');
      if (checkoutButton) {
        event.preventDefault();
        checkoutButton.disabled = true;
        checkoutButton.textContent = 'Opening checkout…';
        try {
          const response = await fetch(`${API}/checkout`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
          const data = await response.json();
          if (!response.ok || !data.checkoutUrl) throw new Error(data.error || 'Checkout is not available yet.');
          window.location.assign(data.checkoutUrl);
        } catch (error) {
          checkoutButton.disabled = false;
          checkoutButton.textContent = 'Checkout reserved items';
          alert(error.message || 'Unable to open checkout.');
        }
        return;
      }
      const qtyButton = event.target.closest('[data-em-page-qty]');
      if (qtyButton) {
        event.preventDefault();
        qtyButton.disabled = true;
        try {
          const response = await fetch(`${API}/update`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ variantId: qtyButton.dataset.variantId, quantity: Number(qtyButton.dataset.quantity) }) });
          const data = await response.json();
          if (!response.ok) throw new Error(data.error || 'Unable to update quantity.');
          const reservation = data.reservation || data;
          await enrichReservationItems(reservation);
          renderPage(page, reservation);
        } catch (error) {
          qtyButton.disabled = false;
          alert(error.message || 'Unable to update your reservation.');
        }
        return;
      }
      const removeButton = event.target.closest('[data-em-page-remove]');
      if (removeButton) {
        event.preventDefault();
        removeButton.disabled = true;
        removeButton.textContent = 'Removing…';
        try {
          const response = await fetch(`${API}/remove`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ variantId: removeButton.dataset.variantId }) });
          const data = await response.json();
          if (!response.ok) throw new Error(data.error || 'Unable to remove this item.');
          const reservation = data.reservation || data;
          await enrichReservationItems(reservation);
          renderPage(page, reservation);
        } catch (error) {
          removeButton.disabled = false;
          removeButton.textContent = 'Remove';
          alert(error.message || 'Unable to update your reservation.');
        }
      }
    });
    loadPage(page);
  }

  function init(container) {
    if (container.dataset.emReady === 'true') return;
    container.dataset.emReady = 'true';
    container.querySelector('[data-em-reserve]')?.addEventListener('click', (event) => reserve(container, event.currentTarget));
    container.addEventListener('click', (event) => {
      const checkoutButton = event.target.closest('[data-em-checkout]');
      if (checkoutButton) { event.preventDefault(); checkout(container, checkoutButton); return; }
      const qtyButton = event.target.closest('[data-em-qty-change]');
      if (qtyButton) { event.preventDefault(); updateQuantity(container, qtyButton); return; }
      const removeButton = event.target.closest('[data-em-remove]');
      if (removeButton) { event.preventDefault(); removeItem(container, removeButton); }
    });
    getStatus(container);
  }

  function resetCheckoutButtons() {
    document.querySelectorAll('[data-em-checkout], [data-em-page-checkout]').forEach((button) => {
      button.disabled = false;
      button.textContent = 'Checkout reserved items';
    });
  }

  function refreshReservationViews() {
    resetCheckoutButtons();
    document.querySelectorAll('[data-em-reservations-page]').forEach((page) => loadPage(page));
    document.querySelectorAll('[data-em-reservation]').forEach((container) => getStatus(container));
  }

  function boot() { document.querySelectorAll('[data-em-reservation]').forEach(init); document.querySelectorAll('[data-em-reservations-page]').forEach(initPage); }
  document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
  window.addEventListener('pageshow', (event) => {
    resetCheckoutButtons();
    if (event.persisted) refreshReservationViews();
  });
})();
